import { useState } from 'react';
import ModalShell from './ModalShell';
import { exportToExcel } from '../utils/exportExcel';
import { cellValue } from '../utils/tableData';
import { useAuth } from '../hooks/useAuth';
import { PORTAL_BTN_SECONDARY } from '../constants/portalUi';
import { useQuery } from '@tanstack/react-query';
import api from '../api';
import CandidateProfileModal from './CandidateProfileModal';
import CandidatePhoneLink from './CandidatePhoneLink';
import { CANDIDATE_PHONE_COLUMN_KEYS, staffCanWhatsAppCandidate } from '../utils/whatsappLink';

export default function InsightModal({ insight, onClose }) {
  const { user } = useAuth();
  const [profileCandidateId, setProfileCandidateId] = useState(null);
  const { data: branding } = useQuery({
    queryKey: ['poster-branding'],
    queryFn: () => api.get('/settings/poster-branding').then(r => r.data),
    enabled: !!user && user.role !== 'candidate'
  });
  if (!insight) return null;

  const { title, subtitle, columns, rows, filename, candidateProfileIds } = insight;
  const isClient = user?.role === 'client';
  const canOpenCandidateProfile = isClient && candidateProfileIds;

  const renderCell = (col, row) => {
    const value = cellValue(col, row);
    const candidateId = row._candidateId || row.id;
    if (
      canOpenCandidateProfile &&
      col.key === 'name' &&
      candidateId &&
      candidateId !== '—'
    ) {
      return (
        <button
          type="button"
          className="text-[hsl(var(--ds-primary))] hover:text-[hsl(var(--ds-primary))] hover:underline underline-offset-2 text-left"
          onClick={() => setProfileCandidateId(candidateId)}
        >
          {value}
        </button>
      );
    }
    if (
      staffCanWhatsAppCandidate(user) &&
      CANDIDATE_PHONE_COLUMN_KEYS.has(col.key) &&
      value &&
      value !== '—'
    ) {
      return (
        <CandidatePhoneLink
          phone={row[col.key] || row.candidatePhone || row.displayPhone}
          stopPropagation={false}
        />
      );
    }
    return value;
  };

  return (
    <>
      <ModalShell
        title={title}
        subtitle={subtitle || `${rows?.length || 0} record(s)`}
        onClose={onClose}
        maxWidth="max-w-5xl"
        zIndex="z-[60]"
        footer={
          <>
            <button
              type="button"
              className={user?.role === 'candidate' ? PORTAL_BTN_SECONDARY : 'px-4 py-2 text-sm border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] hover:bg-[hsl(var(--ds-muted))]'}
              onClick={onClose}
            >
              Close
            </button>
            {user?.role !== 'candidate' && (
              <button
                type="button"
                className="px-4 py-2 text-sm font-medium border border-[hsl(var(--ds-primary))] bg-[hsl(var(--ds-surface))] text-[hsl(var(--ds-primary))] hover:bg-[hsl(var(--ds-primary)/0.08)]"
                onClick={() =>
                  exportToExcel({
                    filename,
                    sheetName: title,
                    columns,
                    rows,
                    companyName:
                      branding?.appName || user?.companyName || user?.sidebarLabel || user?.displayName || 'Paytiemp',
                    reportName: title
                  })
                }
                disabled={!rows?.length}
              >
                Export to Excel
              </button>
            )}
          </>
        }
      >
        {!rows?.length ? (
          <p className="text-center text-[hsl(var(--ds-muted-fg))] py-12">No records for this insight.</p>
        ) : (
          <div className="border border-[hsl(var(--ds-border))] overflow-hidden">
            <div className="overflow-x-auto max-h-[50vh]">
              <table className="w-full text-sm">
                <thead className="bg-[hsl(var(--ds-muted))] border-b border-[hsl(var(--ds-border))] sticky top-0">
                  <tr>
                    <th className="text-left px-3 py-2 font-semibold text-[hsl(var(--ds-fg))] whitespace-nowrap">Sl No</th>
                    {columns.map(col => (
                      <th key={col.key} className="text-left px-3 py-2 font-semibold text-[hsl(var(--ds-fg))] whitespace-nowrap">
                        {col.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, i) => (
                    <tr key={row.id || i} className="border-b border-[hsl(var(--ds-border))] hover:bg-[hsl(var(--ds-muted))]">
                      <td className="px-3 py-2 text-[hsl(var(--ds-muted-fg))] whitespace-nowrap">{i + 1}</td>
                      {columns.map(col => (
                        <td key={col.key} className="px-3 py-2 text-[hsl(var(--ds-fg))] whitespace-nowrap">
                          {renderCell(col, row)}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </ModalShell>

      {profileCandidateId && (
        <CandidateProfileModal
          candidateId={profileCandidateId}
          readOnly
          maskPii
          onClose={() => setProfileCandidateId(null)}
        />
      )}
    </>
  );
}
